import {
  provideHttpClient,
  withInterceptors,
  withInterceptorsFromDi
} from '@angular/common/http';
import { default as ngLang } from '@angular/common/locales/en';
import {
  ApplicationConfig,
  EnvironmentProviders,
  Provider
} from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
  provideRouter,
  withComponentInputBinding,
  withInMemoryScrolling,
  withHashLocation,
  RouterFeatures,
  withViewTransitions
} from '@angular/router';
// import {
//   I18NService,
//   defaultInterceptor,
//   provideBindAuthRefresh,
//   provideStartup
// } from '@core';
import { provideCellWidgets } from '@delon/abc/cell';
import { provideReuseTabConfig } from '@delon/abc/reuse-tab';
import { provideSTWidgets } from '@delon/abc/st';
import {
  authJWTInterceptor,
  authSimpleInterceptor,
  provideAuth
} from '@delon/auth';
import { customJwtInterceptor } from './core/net/custom-jwt.interceptor';
import { siteHeaderInterceptor } from './core/net/site-header.interceptor';
import { provideSFConfig } from '@delon/form';
import {
  AlainProvideLang,
  provideAlain,
  en_US as delonLang
} from '@delon/theme';
import { AlainConfig } from '@delon/util/config';
import { environment } from '@env/environment';
//import { CELL_WIDGETS, SF_WIDGETS, ST_WIDGETS } from '@shared';
import { enUS as dateLang } from 'date-fns/locale';
// import config from 'devextreme/core/config';
import { NzConfig, provideNzConfig } from 'ng-zorro-antd/core/config';
import { NzSafeAny } from 'ng-zorro-antd/core/types';
import { en_US as zorroLang } from 'ng-zorro-antd/i18n';

//import { routes } from './routes/routes';
import { provideStartup } from './core/startup/startup.service';
import { provideTokenRefresh } from './core/util/token-refresh.service';
// import { licenseKey } from '../devextreme-license';
import { ICONS } from '../style-icons';
import { ICONS_AUTO } from '../style-icons-auto';

// config({ licenseKey }); // Set the license key for DevExtreme - MOVED TO LAZY LOADED MODULES

const defaultLang: AlainProvideLang = {
  abbr: 'en-US',
  ng: ngLang,
  zorro: zorroLang,
  date: dateLang,
  delon: delonLang
};

const alainConfig: AlainConfig = {
  acl: {
    //guard_url: 'exception/401',
    guard_url: 'dashboard',
    preCan: (roleOrAbility: any) => {
      if (roleOrAbility && typeof roleOrAbility === 'string') {
        const ability: string = roleOrAbility.toString();
        return { ability: [ability] };
      }
      return roleOrAbility;
    }
  },
  loading: {
    text: '',
    delay: 500
  },
  st: {
    modal: { size: 'lg' },
    loadingDelay: 0,
    //loadingIndicator: templates.stIndicator,
    size: 'small',
    ps: 10,
    page: {
      zeroIndexed: false,
      showSize: true
    },
    req: {
      type: 'skip',
      method: 'GET',
      reName: {
        skip: '$skip',
        limit: '$top'
      },
      lazyLoad: false,
      process: (reqOptions: NzSafeAny) => {
        return reqOptions;
      }
    },
    res: {
      reName: {
        total: 'count',
        list: 'value'
      }
    },
    sortReName: {
      ascend: 'asc',
      descend: 'desc'
    },
    multiSort: {
      key: '$orderby',
      separator: ',',
      nameSeparator: ' ',
      arrayParam: false,
      keepEmptyKey: false
    }
  },
  pageHeader: { homeI18n: 'Home' },
  lodop: {
    license: `A59B099A586B3851E0F0D7FDBF37B603`,
    licenseA: `C94CEE276DB2187AE6B65D56B3FC2848`
  },
  auth: {
    login_url: '/passport/login',
    token_send_key: 'Authorization',
    token_send_template: 'Bearer ${token}',
    refreshTime: 3000,
    refreshOffset: 6000,
    store_key: 'qca_token'
  },
  utilCurrency: {
    ingoreZeroPrecision: false,
    precision: 2
  },
  xlsx: {
    url: 'https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.12.13/xlsx.full.min.js'
  }
};

const ngZorroConfig: NzConfig = {
  message: {
    nzTop: '64px',
    nzDuration: 3000,
    nzPauseOnHover: true,
    nzAnimate: true
  },
  notification: {
    nzTop: '64px',
    nzDuration: 3000,
    nzPauseOnHover: true,
    nzAnimate: true
  },
  tabs: {
    nzAnimated: false
  }
};

const routerFeatures: RouterFeatures[] = [
  withComponentInputBinding(),
  withViewTransitions(),
  withInMemoryScrolling({ scrollPositionRestoration: 'top' })
];
if (environment.useHash) routerFeatures.push(withHashLocation());

const providers: Array<Provider | EnvironmentProviders> = [
  // migrated from global-config.module.ts
  ...(environment.providers || []),
  provideHttpClient(
    withInterceptors([
      siteHeaderInterceptor,
      ...(environment.interceptorFns ?? []),
      // authSimpleInterceptor,
      customJwtInterceptor // Use our custom JWT interceptor instead of authJWTInterceptor
    ]),
    withInterceptorsFromDi()
  ),
  provideNzConfig(ngZorroConfig),
  provideAlain({
    config: alainConfig,
    defaultLang,
    //i18nClass: I18NService,
    icons: [...ICONS_AUTO, ...ICONS]
  }),
  provideReuseTabConfig({ debug: false, mode: 2, max: 10 }),
  // migrated from global-config.module.ts
  provideAnimations(),
  //provideRouter(routes, ...routerFeatures),
  provideAuth(),
  //provideCellWidgets(...CELL_WIDGETS),
  //provideSTWidgets(...ST_WIDGETS),
  //provideSFConfig({ widgets: SF_WIDGETS }),
  provideStartup(),
  provideTokenRefresh()
];

// If you use `@delon/auth` to refresh the token, additional registration `provideBindAuthRefresh` is required
// if (
//   environment.api?.refreshTokenEnabled &&
//   environment.api.refreshTokenType === 'auth-refresh'
// ) {
//   providers.push(provideBindAuthRefresh());
// }

export const appConfig: ApplicationConfig = {
  providers: providers
};
